/**
 * CCK free-credit user UI (cck-credits-01 / T4): a redeem field, the billing
 * "Credits" area, and the post-signup redeem popup. Dense, one primary action,
 * progressive disclosure — per the global UX rules.
 */
import React, { useEffect, useState } from 'react';

import { cckCreditsApi } from '../services/api';
import { CckCreditsMe, CckLedgerEntry, CckRedeemResult } from '../types';
import { expiryLabel, isExpiringSoon } from '../services/cckCreditsUtil';

// ── Redeem field ─────────────────────────────────────────────────────────────
export const RedeemField: React.FC<{
  onSuccess: (r: CckRedeemResult) => void;
  autoFocus?: boolean;
}> = ({ onSuccess, autoFocus }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const c = code.trim();
    if (!c) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await cckCreditsApi.redeem(c);
      setOk(`✅ ${r.credits_granted.toLocaleString()} credits added.`);
      setCode('');
      onSuccess(r);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not redeem that code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <input
          value={code}
          autoFocus={autoFocus}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Enter a CCK / coupon code"
          aria-label="Coupon code"
          style={fieldStyle}
        />
        <button type="submit" disabled={busy || !code.trim()} className="sidebar-new-run-btn">
          {busy ? 'Redeeming…' : 'Redeem'}
        </button>
      </div>
      {err && <div style={{ color: 'var(--category-dead-fg)', fontSize: 12, marginTop: 6 }}>❌ {err}</div>}
      {ok && <div style={{ color: 'var(--category-good-fg)', fontSize: 12, marginTop: 6 }}>{ok}</div>}
    </form>
  );
};

// ── Billing "Credits" area (D-8) ─────────────────────────────────────────────
export const CckCreditsPanel: React.FC = () => {
  const [me, setMe] = useState<CckCreditsMe | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    cckCreditsApi.getMe().then(setMe).catch(() => setMe(null)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <div style={{ color: 'var(--text-faint)' }}>Loading credits…</div>;
  if (!me || !me.enabled) return null; // feature dark → no Credits area

  const exp = expiryLabel(me);
  const warn = isExpiringSoon(me);
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div style={balanceStyle}>{me.balance.toLocaleString()} <span style={{ fontSize: 13, color: 'var(--text-faint)' }}>credits</span></div>
        {exp && (
          <div style={{ fontFamily: 'var(--font-data)', fontSize: 12, letterSpacing: '0.04em',
            color: warn ? 'var(--amber)' : 'var(--text-faint)' }}>
            {exp}
          </div>
        )}
      </div>

      <div style={usageRow}>
        <span>{me.total_used.toLocaleString()} used · {me.total_granted.toLocaleString()} granted</span>
        <span>{me.jobs} runs · {me.uploads} uploads</span>
      </div>

      <RedeemField onSuccess={load} />
    </div>
  );
};

// ── Credit logs (movement history) ────────────────────────────────────────────
const REASON_LABEL: Record<string, string> = {
  coupon_grant: 'Code redeemed',
  admin_grant: 'Admin grant',
  job_reserve: 'Run started',
  job_refund: 'Refund (undialed)',
  expiry: 'Expired',
};

export const CckCreditLogs: React.FC = () => {
  const [entries, setEntries] = useState<CckLedgerEntry[] | null>(null);

  useEffect(() => {
    let alive = true;
    cckCreditsApi.getHistory()
      .then((r) => { if (alive) setEntries(r.entries); })
      .catch(() => { if (alive) setEntries([]); });
    return () => { alive = false; };
  }, []);

  if (entries == null) return <div style={{ color: 'var(--text-faint)', marginTop: 14 }}>Loading activity…</div>;
  if (entries.length === 0) return null;

  const fmt = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
      ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ marginTop: 18, maxWidth: 560 }}>
      <h4 style={{ margin: '0 0 8px', color: 'var(--text-soft)', fontSize: 12,
        textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'var(--font-data)' }}>
        Credit activity
      </h4>
      <div style={{ border: '1px solid var(--border)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {entries.map((e, i) => {
              const pos = e.delta >= 0;
              return (
                <tr key={i} style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}>
                  <td style={{ padding: '7px 12px', color: 'var(--text)' }}>{REASON_LABEL[e.reason] ?? e.reason}</td>
                  <td style={{ padding: '7px 12px', textAlign: 'right', fontFamily: 'var(--font-data)',
                    color: pos ? 'var(--category-good-fg)' : 'var(--text-soft)' }}>
                    {pos ? '+' : ''}{e.delta.toLocaleString()}
                  </td>
                  <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--text-faint)',
                    fontFamily: 'var(--font-data)', fontSize: 11, whiteSpace: 'nowrap' }}>
                    {fmt(e.created_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Post-signup popup ────────────────────────────────────────────────────────
const DISMISS_KEY = 'cck_redeem_dismissed';

export const PostSignupCreditsPopup: React.FC = () => {
  const [show, setShow] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) return;
    let alive = true;
    cckCreditsApi.getMe()
      .then((me) => {
        // Show only for enabled, brand-new users with no credits yet.
        if (alive && me.enabled && me.balance === 0 && me.total_granted === 0) setShow(true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  };

  if (!show) return null;
  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Redeem a code for free credits">
      <div style={modalStyle}>
        <h3 style={{ margin: '0 0 6px', color: 'var(--text-strong)', fontFamily: 'var(--font-display)', letterSpacing: '0.03em' }}>
          Have a CCK code?
        </h3>
        <p style={{ margin: '0 0 12px', color: 'var(--text-soft)', fontSize: 13 }}>
          Enter it for free trial credits — no card needed. 1 credit = 1 recording.
        </p>
        {balance != null ? (
          <div style={{ color: 'var(--category-good-fg)', fontSize: 14, marginBottom: 12 }}>
            🎉 You now have <strong>{balance.toLocaleString()}</strong> credits.
          </div>
        ) : (
          <RedeemField autoFocus onSuccess={(r) => setBalance(r.balance)} />
        )}
        <div style={{ marginTop: 14, textAlign: 'right' }}>
          <button onClick={dismiss} style={skipBtnStyle}>
            {balance != null ? 'Done' : 'Skip for now'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── styles ───────────────────────────────────────────────────────────────────
const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', border: '2px solid var(--border)',
  boxShadow: '6px 6px 0 rgba(0,0,0,0.5)', padding: '18px 20px', maxWidth: 560,
};
const balanceStyle: React.CSSProperties = {
  color: 'var(--text-strong)', fontFamily: 'var(--font-display)', fontSize: 26, letterSpacing: '0.02em',
};
const usageRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
  fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-faint)',
  letterSpacing: '0.04em', margin: '10px 0 4px',
};
const fieldStyle: React.CSSProperties = {
  flex: '1 1 auto', padding: '8px 10px', background: 'var(--surface-subtle)', color: 'var(--text)',
  border: '1px solid var(--border)', outline: 'none', textTransform: 'uppercase', letterSpacing: '0.04em',
};
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const modalStyle: React.CSSProperties = {
  background: 'var(--surface)', border: '2px solid var(--border)',
  boxShadow: '8px 8px 0 rgba(0,0,0,0.6)', padding: '22px 24px', width: 'min(460px, 100%)',
};
const skipBtnStyle: React.CSSProperties = {
  background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)',
  padding: '6px 12px', cursor: 'pointer', fontSize: 12,
};
